/**
 * Headers for Stratus Labs API calls.
 *
 * Always send **X-User-Email** (lowercase) together with **X-User-Id** when the email is known.
 * The Functions API resolves the canonical Labs user by email first, so auth provider changes
 * (Entra → WorkOS, different WorkOS environments) reuse the same MongoDB user record.
 *
 * @see docs/API_IDENTITY.md
 */
export function normalizeLabsEmail(email: string | null | undefined): string {
  return (email ?? "").trim().toLowerCase();
}

/**
 * Build identity headers for Labs API requests.
 * @param authUserId - Auth provider user id (WorkOS, Entra oid, SWA userId).
 * @param email - User email; normalized to lowercase in X-User-Email.
 */
export function labsApiHeaders(
  authUserId: string,
  email?: string | null
): Record<string, string> {
  const headers: Record<string, string> = { "X-User-Id": authUserId };
  const normalized = normalizeLabsEmail(email);
  if (normalized) headers["X-User-Email"] = normalized;
  return headers;
}

/** Labs user profile fields returned by POST /api/users/sync and GET /api/users/me. */
export type LabsUserProfile = {
  id: string;
  email: string;
  displayName: string;
  isAdmin: boolean;
  isDisabled: boolean;
  siteAccessApproved: boolean;
};

/** Parse Labs user JSON (PascalCase or camelCase) from the Functions API. */
export function parseLabsUserProfile(data: Record<string, unknown>): LabsUserProfile | null {
  const id = String(data.id ?? data.Id ?? "").trim();
  if (!id) return null;
  const email = normalizeLabsEmail(String(data.email ?? data.Email ?? ""));
  const displayName = String(data.displayName ?? data.DisplayName ?? "").trim();
  return {
    id,
    email,
    displayName: displayName || email,
    isAdmin: !!(data.isAdmin ?? data.IsAdmin),
    isDisabled: !!(data.isDisabled ?? data.IsDisabled),
    siteAccessApproved: !!(data.siteAccessApproved ?? data.SiteAccessApproved),
  };
}

/** POST /api/users/sync — returns canonical Labs user from the database. */
export async function syncLabsUser(
  apiBaseUrl: string,
  authUserId: string,
  email: string,
  displayName?: string | null
): Promise<LabsUserProfile | null> {
  const base = apiBaseUrl.replace(/\/$/, "");
  const normalizedEmail = normalizeLabsEmail(email);
  if (!normalizedEmail) return null;

  let res: Response;
  try {
    res = await fetch(`${base}/api/users/sync`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userId: authUserId,
        email: normalizedEmail,
        displayName: displayName?.trim() || normalizedEmail,
      }),
    });
  } catch (err) {
    console.error(
      `[labsApi] POST /api/users/sync network error for ${normalizedEmail} (${base}):`,
      err
    );
    return null;
  }
  if (!res.ok) {
    const errBody = await res.text().catch(() => "");
    console.error(
      `[labsApi] POST /api/users/sync failed ${res.status} for ${normalizedEmail}:`,
      errBody || res.statusText
    );
    return null;
  }

  const data = (await res.json()) as Record<string, unknown>;
  return parseLabsUserProfile(data);
}
